/**
 * GivePage — public donation page at /give/:slug.
 *
 * Renders a focused mini-GoFundMe layout: hero image, title, story, live
 * funding progress bar, suggested amounts → Stripe checkout, bank details,
 * and a recent-gifts feed.
 */
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, HandHeart, Heart, ChevronDown, Share2, Copy, Facebook, Twitter, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { formatCurrency as fmtCurrency } from '@/lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GivePage = () => {
  const { slug } = useParams();
  const lowerSlug = (slug || '').toLowerCase();

  const { data: page, isLoading, error } = useQuery({
    queryKey: ['donation-page', lowerSlug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/donation-pages/public/${lowerSlug}`);
      if (!res.ok) throw new Error('Not found');
      return res.json();
    },
    retry: false,
  });

  const { data: progress = { raised: 0, donors: 0, recent: [] }, refetch: refetchProgress } = useQuery({
    queryKey: ['donation-page-progress', lowerSlug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/donation-pages/public/${lowerSlug}/progress`);
      if (!res.ok) return { raised: 0, donors: 0, recent: [] };
      return res.json();
    },
    enabled: !!page,
    refetchInterval: 30_000,
  });

  // Detect Stripe redirect and confirm session.
  React.useEffect(() => {
    if (!page) return;
    const params = new URLSearchParams(window.location.search);
    const donation = params.get('donation');
    const sid = params.get('session_id');
    if (donation === 'success' && sid) {
      fetch(`${API_URL}/api/donation-pages/public/${lowerSlug}/donate/status/${sid}`)
        .then((r) => r.json())
        .then((d) => {
          if (d.payment_status === 'paid') {
            toast.success('Thank you for your gift! Your donation has been received.');
            refetchProgress();
          } else {
            toast.info('Donation pending — we will confirm shortly.');
          }
        })
        .catch(() => {});
    } else if (donation === 'cancelled') {
      toast.info('Donation cancelled. No charges were made.');
    }
  }, [page, lowerSlug, refetchProgress]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="give-loading">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="give-404">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-10 text-center">
            <Heart className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <h1 className="font-bold text-lg mb-1">Donation Page Not Found</h1>
            <p className="text-sm text-slate-500">We couldn&apos;t find a page at <code>{lowerSlug}</code>.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const theme = {
    primary: page.theme_primary || '#0f766e',
    accent: page.theme_accent || '#f97316',
  };

  return (
    <div className="min-h-screen bg-white" data-testid="give-page" style={{ '--g-primary': theme.primary, '--g-accent': theme.accent }}>
      {/* Hero */}
      <div className="relative" data-testid="give-hero">
        {page.hero_image_url ? (
          <div
            className="h-[55vh] min-h-[420px] bg-cover bg-center"
            style={{ backgroundImage: `linear-gradient(rgba(15,23,42,0.45), rgba(15,23,42,0.7)), url(${page.hero_image_url})` }}
          />
        ) : (
          <div className="h-[55vh] min-h-[420px]" style={{ backgroundColor: theme.primary }} />
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6">
          {page.organizer_name && (
            <p className="text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: theme.accent }}>
              Organised by {page.organizer_name}
            </p>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold mt-3 max-w-3xl">{page.title}</h1>
          {page.subtitle && <p className="mt-4 text-lg opacity-90 max-w-2xl">{page.subtitle}</p>}
          <Button
            size="lg"
            onClick={() => document.querySelector('#give-widget')?.scrollIntoView({ behavior: 'smooth' })}
            className="mt-7 text-white"
            style={{ backgroundColor: theme.accent }}
            data-testid="give-hero-cta"
          >
            <HandHeart className="h-4 w-4 mr-2" /> Donate Now
          </Button>
        </div>
        <ChevronDown className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 h-7 w-7 animate-bounce" />
      </div>

      {/* Body grid */}
      <div className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-10">
        {/* Story */}
        <div className="lg:col-span-2 prose prose-slate max-w-none">
          {page.story_markdown
            ? <StorySection text={page.story_markdown} theme={theme} />
            : <p className="text-slate-600">Story coming soon…</p>
          }
        </div>

        {/* Give widget */}
        <aside className="lg:sticky lg:top-6 self-start space-y-6">
          <GiveWidget page={page} progress={progress} theme={theme} slug={lowerSlug} onSuccess={refetchProgress} />
          <ShareCard page={page} progress={progress} theme={theme} slug={lowerSlug} />
          <RecentGifts recent={progress.recent} theme={theme} />
        </aside>
      </div>
    </div>
  );
};

const StorySection = ({ text, theme }) => (
  <div data-testid="give-story">
    <h2 className="text-2xl font-bold mb-4" style={{ color: theme.primary }}>The Story</h2>
    <div className="text-slate-700 leading-relaxed whitespace-pre-line">{text}</div>
  </div>
);

const GiveWidget = ({ page, progress, theme, slug, onSuccess }) => {
  const goal = Number(page.goal_amount || 0);
  const raised = Number(progress.raised || 0);
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const currency = (page.currency || 'USD').toUpperCase();
  const fmt = (n) => fmtCurrency(n, currency, { decimals: 0, showCode: true });

  const suggested = page.suggested_amounts && page.suggested_amounts.length ? page.suggested_amounts : [25, 50, 100, 250];
  const [amount, setAmount] = React.useState(suggested[1] || 50);
  const [custom, setCustom] = React.useState('');
  const [donorName, setDonorName] = React.useState('');
  const [donorEmail, setDonorEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [anon, setAnon] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const give = async () => {
    const final = Number(custom) > 0 ? Number(custom) : Number(amount);
    if (!final || final < 1) {
      toast.error('Please choose an amount of at least 1.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/donation-pages/public/${slug}/donate/stripe-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: final,
          donor_name: donorName,
          donor_email: donorEmail,
          message,
          is_anonymous: anon,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not start checkout');
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card id="give-widget" className="shadow-md" data-testid="give-widget">
      <CardContent className="p-6 space-y-4">
        {/* Progress */}
        <div>
          <div className="flex items-end justify-between mb-1">
            <p className="text-2xl font-bold" style={{ color: theme.primary }}>{fmt(raised)}</p>
            {goal > 0 && <p className="text-xs text-slate-500">of {fmt(goal)}</p>}
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: theme.accent }} data-testid="give-progress-bar" />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>{pct}% funded</span>
            <span>{progress.donors || 0} donor{progress.donors === 1 ? '' : 's'}</span>
          </div>
        </div>

        {/* Amount pills */}
        <div className="flex flex-wrap gap-2">
          {suggested.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => { setAmount(v); setCustom(''); }}
              data-testid={`give-amount-${v}`}
              className={`px-4 py-2 rounded-full border text-sm font-semibold transition ${
                amount === v && !custom ? 'text-white' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
              style={amount === v && !custom ? { backgroundColor: theme.accent, borderColor: theme.accent } : undefined}
            >
              {currency} {v}
            </button>
          ))}
          <input
            type="number"
            min={1}
            placeholder="Other"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            className="px-3 py-2 rounded-full border border-slate-300 text-sm w-24"
            data-testid="give-custom-amount"
          />
        </div>

        {/* Donor details */}
        <div className="grid gap-2">
          <input
            type="text"
            placeholder="Your name (optional)"
            value={donorName}
            onChange={(e) => setDonorName(e.target.value)}
            className="px-3 py-2 rounded border border-slate-300 text-sm"
            data-testid="give-name"
          />
          <input
            type="email"
            placeholder="Email for receipt (optional)"
            value={donorEmail}
            onChange={(e) => setDonorEmail(e.target.value)}
            className="px-3 py-2 rounded border border-slate-300 text-sm"
            data-testid="give-email"
          />
          <textarea
            placeholder="A message of support (optional)"
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="px-3 py-2 rounded border border-slate-300 text-sm"
            data-testid="give-message"
          />
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} data-testid="give-anonymous" />
            Give anonymously
          </label>
        </div>

        <Button
          size="lg"
          onClick={give}
          disabled={loading}
          className="w-full text-white"
          style={{ backgroundColor: theme.accent }}
          data-testid="give-submit"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <HandHeart className="h-4 w-4 mr-2" />}
          Give {currency} {Number(custom) > 0 ? Number(custom) : amount}
        </Button>

        {page.bank_details && (
          <details className="border-t pt-3" data-testid="give-bank-details">
            <summary className="text-xs font-semibold text-slate-600 cursor-pointer">Or give by bank transfer</summary>
            <pre className="text-xs whitespace-pre-wrap font-sans text-slate-700 mt-2">{page.bank_details}</pre>
            {page.whatsapp_confirm && (
              <a
                href={`https://wa.me/${String(page.whatsapp_confirm).replace(/\D/g, '')}?text=${encodeURIComponent('Hello, I have made a donation via bank transfer. Please find proof of payment attached.')}`}
                target="_blank"
                rel="noreferrer"
                className="block mt-2 text-xs underline"
                style={{ color: theme.accent }}
              >
                Send proof on WhatsApp →
              </a>
            )}
          </details>
        )}
      </CardContent>
    </Card>
  );
};

const ShareCard = ({ page, progress, theme, slug }) => {
  const url = `${window.location.origin}/give/${slug}`;
  const goal = Number(page.goal_amount || 0);
  const raised = Number(progress.raised || 0);
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const currency = (page.currency || 'USD').toUpperCase();
  const fmt = (n) => fmtCurrency(n, currency, { decimals: 0, showCode: true });

  // Pre-filled share copy — short, action-oriented, includes status.
  const blurb = pct > 0
    ? `${page.title} is ${pct}% funded (${fmt(raised)} raised). Let's help them reach the goal 🙏 ${url}`
    : `${page.title} — help us reach our goal of ${fmt(goal)} 🙏 ${url}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(blurb);
      toast.success('Share message copied — paste it anywhere.');
    } catch {
      // Fallback to a prompt so iOS/Safari without permissions still works.
      window.prompt('Copy this share message:', blurb);
    }
  };

  const openShare = (network) => {
    const enc = encodeURIComponent;
    const urls = {
      whatsapp: `https://wa.me/?text=${enc(blurb)}`,
      twitter:  `https://twitter.com/intent/tweet?text=${enc(blurb)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(blurb)}`,
    };
    window.open(urls[network], '_blank', 'noopener,width=600,height=520');
  };

  return (
    <Card data-testid="give-share-card">
      <CardContent className="p-5 space-y-3">
        <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: theme.primary }}>
          <Share2 className="h-4 w-4" /> Help us go further
        </h3>
        <p className="text-xs text-slate-500">Every share doubles the chances of hitting the goal.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button
            type="button"
            onClick={() => openShare('whatsapp')}
            className="flex flex-col items-center gap-1 py-2 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[10px] font-semibold transition"
            data-testid="give-share-whatsapp"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </button>
          <button
            type="button"
            onClick={() => openShare('twitter')}
            className="flex flex-col items-center gap-1 py-2 rounded-md bg-sky-50 hover:bg-sky-100 text-sky-700 text-[10px] font-semibold transition"
            data-testid="give-share-twitter"
          >
            <Twitter className="h-4 w-4" /> Twitter
          </button>
          <button
            type="button"
            onClick={() => openShare('facebook')}
            className="flex flex-col items-center gap-1 py-2 rounded-md bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[10px] font-semibold transition"
            data-testid="give-share-facebook"
          >
            <Facebook className="h-4 w-4" /> Facebook
          </button>
          <button
            type="button"
            onClick={copy}
            className="flex flex-col items-center gap-1 py-2 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-700 text-[10px] font-semibold transition"
            data-testid="give-share-copy"
          >
            <Copy className="h-4 w-4" /> Copy
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

const RecentGifts = ({ recent, theme }) => {  if (!recent || !recent.length) return null;
  return (
    <Card data-testid="give-recent">
      <CardContent className="p-5">
        <h3 className="text-sm font-bold mb-3" style={{ color: theme.primary }}>Recent Gifts</h3>
        <ul className="space-y-2 text-sm">
          {recent.slice(0, 8).map((r, i) => (
            <li key={i} className="flex items-center justify-between border-b last:border-b-0 pb-2 last:pb-0">
              <div>
                <p className="font-semibold text-slate-700">{r.donor_name}</p>
                {r.message && <p className="text-xs text-slate-500 italic">&ldquo;{r.message}&rdquo;</p>}
              </div>
              <p className="font-semibold" style={{ color: theme.accent }}>
                {fmtCurrency(r.amount, r.currency || 'USD', { decimals: 0, showCode: true })}
              </p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default GivePage;
